import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*

// A league sessionKey denotes a whole round; refId identifies the actual match.
fun StatGame.eventKey(): String = "$source:$refId"

@Composable
fun FrameStatsView(games: List<StatGame>, details: List<DetailedGame>, names: Map<String, String>, playerProfile: Boolean = false, selectedEvent: String? = null) {
    if (selectedEvent != null) {
        SessionFrameStats(games, details, names, selectedEvent)
    } else {
        val selected = seasonComparisonGames(games, details)
        val metrics = bowlingMetrics(selected)
        FrameStatsPanel(metrics, selected.size, games.size)
        if (playerProfile) PlayerProfileSummary(metrics)
    }
}

/** The reference follows the page filters, never the selected event or game. */
internal fun seasonComparisonGames(filteredGames: List<StatGame>, details: List<DetailedGame>): List<DetailedGame> {
    val keys = filteredGames.map { "${it.playerId}:${it.sheetKey()}:${it.gameType}" }.toSet()
    return details.filter { "${it.game.playerId}:${it.game.sheetKey()}:${it.game.gameType}" in keys }
}

@Composable
fun SessionFrameStats(games: List<StatGame>, details: List<DetailedGame>, names: Map<String, String>, selectedEvent: String) {
    var selectedGame by remember(selectedEvent) { mutableStateOf("") }
    val event = filterStatEvent(games, selectedEvent).takeIf { it.isNotEmpty() } ?: return
    H4 { Text("Framy tréninku / zápasu / turnaje") }
    val keys = event.map { "${it.playerId}:${it.sheetKey()}" }.toSet()
    val eventDetails = details.filter { "${it.game.playerId}:${it.game.sheetKey()}" in keys }
    val reference = bowlingMetrics(seasonComparisonGames(games, details))
    val chosen = event.firstOrNull { "${it.playerId}:${it.sheetKey()}" == selectedGame }
    val displayed = if (chosen == null) eventDetails else eventDetails.filter {
        it.game.playerId == chosen.playerId && it.game.sheetKey() == chosen.sheetKey()
    }
    Label(attrs = { classes("session-frame-filter") }) {
        Text(" Zobrazení ")
        Select(attrs = { classes("field"); onChange { selectedGame = it.value ?: "" } }) {
            Option("", attrs = { if (chosen == null) selected() }) { Text("Celá událost") }
            event.sortedWith(compareBy<StatGame> { it.gameNo }.thenBy { names[it.playerId] ?: it.playerId }).forEach { g ->
                val id = "${g.playerId}:${g.sheetKey()}"
                Option(id, attrs = { if (selectedGame == id) selected() }) {
                    Text("${g.gameNo}. hra · ${names[g.playerId] ?: g.playerId} · ${g.score}")
                }
            }
        }
    }
    val metrics = bowlingMetrics(displayed)
    FrameStatsPanel(metrics, displayed.size, if (chosen == null) event.size else 1, reference)
    CumulativeScoreChart(displayed, seasonComparisonGames(games, details), names)
    if (displayed.isNotEmpty()) {
        H4 { Text("Porovnání se sezónou") }
        P(attrs = { classes("muted") }) {
            Text("Referenční vzorek podle horních filtrů (sezóna, zdroje, typ a případně hráč): ${reference.games} zápisů. Při výběru události nebo hry zůstává stejný a zahrnuje i vybranou událost. Při filtru Všechny sezóny zahrnuje celé zvolené období; rozdíly procent jsou v procentních bodech.")
        }
        if (reference.games > 0) MetricsSeasonComparison(metrics, reference)
        else P(attrs = { classes("muted") }) { Text("Pro sezónní porovnání nejsou dostupné zápisy s určenou sezónou.") }
    }
    H4 { Text("Porovnání jednotlivých her") }
    Div(attrs = { classes("stats-table-wrap") }) {
        Table(attrs = { classes("stats-table") }) {
            Thead { Tr { listOf("Hráč / hra", "Nához", "Strike", "Spare", "Open", "1. hod Ø").forEach { Th { Text(it) } } } }
            Tbody {
                event.sortedWith(compareBy<StatGame> { it.gameNo }.thenBy { names[it.playerId] ?: it.playerId }).forEach { g ->
                    val d = eventDetails.filter { it.game.playerId == g.playerId && it.game.sheetKey() == g.sheetKey() }
                    val m = bowlingMetrics(d)
                    Tr {
                        Td {
                            Button(attrs = { classes("small-btn"); onClick { selectedGame = "${g.playerId}:${g.sheetKey()}" } }) {
                                Text("${names[g.playerId] ?: g.playerId} · ${g.gameNo}. hra")
                            }
                        }
                        Td { Text("${g.score}") }
                        Td { Text(percentage(m.strikes, m.firstBallAttempts)) }
                        Td { Text(percentage(m.spares, m.spareOpportunities)) }
                        Td { Text(percentage(m.opens, m.resolvedFrames)) }
                        Td { Text(m.firstBallAverage?.toString() ?: "–") }
                    }
                }
            }
        }
    }
    P(attrs = { classes("muted") }) { Text("Kliknutím na hru zobrazíš její úplné frameové statistiky a porovnání se stejným sezónním průměrem podle horních filtrů. Pomlčka znamená chybějící zápis nebo žádnou příležitost.") }
}

private data class ComparisonMetric(val label: String, val value: Double?, val season: Double?, val percent: Boolean = true)
private fun rate(part: Int, whole: Int): Double? = if (whole > 0) part * 100.0 / whole else null
internal fun signedDifference(value: Double, reference: Double): String {
    val difference = kotlin.math.round((value - reference) * 10) / 10.0
    return if (difference > 0) "+$difference" else if (difference == 0.0) "0.0" else "$difference"
}

@Composable
private fun MetricsSeasonComparison(m: BowlingMetrics, s: BowlingMetrics) {
    val rows = listOf(
        ComparisonMetric("Strike", rate(m.strikes, m.firstBallAttempts), rate(s.strikes, s.firstBallAttempts)),
        ComparisonMetric("Spare", rate(m.spares, m.spareOpportunities), rate(s.spares, s.spareOpportunities)),
        ComparisonMetric("Open", rate(m.opens, m.resolvedFrames), rate(s.opens, s.resolvedFrames)),
        ComparisonMetric("Dohozené splity", rate(m.convertedSplits, m.splitSpareOpportunities), rate(s.convertedSplits, s.splitSpareOpportunities)),
        ComparisonMetric("1. hod Ø", m.firstBallAverage, s.firstBallAverage, false),
        ComparisonMetric("Čisté hry", rate(m.cleanGames, m.games), rate(s.cleanGames, s.games)),
        ComparisonMetric("Nejdelší série striků", m.longestStrikeRun.toDouble(), s.longestStrikeRun.toDouble(), false),
        ComparisonMetric("Doubly / hra", m.doubles.toDouble() / m.games, s.doubles.toDouble() / s.games, false),
        ComparisonMetric("Turkey / hra", m.turkeys.toDouble() / m.games, s.turkeys.toDouble() / s.games, false),
    )
    Div(attrs = { classes("stats-table-wrap") }) {
        Table(attrs = { classes("stats-table") }) {
            Thead { Tr { listOf("Ukazatel", "Výběr", "Sezóna", "Rozdíl").forEach { Th { Text(it) } } } }
            Tbody {
                rows.forEach { r ->
                    fun format(v: Double?): String = v?.let { "${round1(it)}${if (r.percent) " %" else ""}" } ?: "–"
                    Tr {
                        Td { Text(r.label) }; Td { Text(format(r.value)) }; Td { Text(format(r.season)) }
                        Td { Text(if (r.value != null && r.season != null) "${signedDifference(r.value, r.season)}${if (r.percent) " p. b." else ""}" else "–") }
                    }
                }
            }
        }
    }
    P(attrs = { classes("muted") }) { Text("Čisté hry jsou podíl her; doubly a turkey jsou přepočtené na hru. Nejdelší série se porovnává se sezónním maximem.") }
}
